import type { CallSessionStatus } from "@/types/call";

export type DoctorStatus = "pending" | "approved" | "rejected";

export interface ScheduleSlot {
  day: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

export interface Doctor {
  id: string;
  legacyMongoId: string | null;
  name: string;
  specialization: string;
  experience: number;
  fees: number;
  location: string;
  rating: number;
  bio: string | null;
  phone: string | null;
  email: string | null;
  profilePicture: string | null;
  hospitalName: string | null;
  qualifications: string[];
  languages: string[];
  schedule: ScheduleSlot[];
  verified: boolean;
  status: DoctorStatus;
  videoConsultationAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * `missed` is a real server status as of backend_update.md §4 — a sweep flips
 * `booked → missed` within 5 minutes of a slot ending with no attended call.
 * The portal no longer derives it, but still falls back to the clock inside
 * that sweep window (see `resolveBookingView`).
 */
export type BookingStatus = "booked" | "completed" | "cancelled" | "missed";
export type ConsultationType = "in_person" | "video";

/** Most recent CallSession for a booking; `none` when no call was ever started. */
export type LastCallStatus = "none" | CallSessionStatus;

export interface Booking {
  id: string;
  legacyMongoId: string | null;
  patientName: string;
  patientEmail: string | null;
  patientPhone: string | null;
  patientAge: string | null;
  patientGender: string | null;
  relationship: string | null;
  fees: number;
  /** Midnight UTC on the appointment's day — a date, not the time of the slot. */
  appointmentDate: string;
  /** "YYYY-MM-DD" in the patient's zone. */
  dateLocal: string;
  /** IANA zone, or null when the backend did not record one. */
  timeZone: string | null;
  /** Wall clock, "HH:mm" — no date and no zone attached. */
  startTime: string;
  endTime: string;
  status: BookingStatus;
  consultationType: ConsultationType;
  createdAt: string;

  /*
   * Call attendance, server-computed (backend_update.md §3). These replaced the
   * portal's per-device localStorage record of which calls it saw attended, so
   * a doctor now classifies identically on every machine they sign in from.
   *
   * Typed as present because the contract says every booking carries them, but
   * every read below tolerates their absence: staging can still be serving the
   * older payload while this ships.
   */
  lastCallStatus: LastCallStatus;
  /** ISO timestamp of the patient's first join; null if they never joined. */
  patientJoinedAt: string | null;
  /** Duration of the most recent ended session, in seconds. */
  callDurationSeconds: number | null;
}

export interface DoctorStats {
  totalBookings: number;
  upcomingBookings: number;
  todayBookings: number;
  video: number;
  inPerson: number;
}

export interface RegisterDoctorInput {
  name: string;
  specialization: string;
  experience: number;
  fees?: number;
  location?: string;
  bio?: string | null;
  phone?: string | null;
  hospitalName?: string | null;
  profilePicture?: string | null;
  videoConsultationAvailable?: boolean;
  qualifications?: string[];
  languages?: string[];
  schedule?: ScheduleSlot[];
}

export type UpdateDoctorInput = Partial<RegisterDoctorInput>;

export const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;
